import React, { useState, useMemo } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import {
  ElectricalRun,
  ElectricalRunResult,
  ElectricalCalculatorResults,
  WireMaterial,
  WireType,
  ProtectionType,
  AmbientTemp,
  WireGauge,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import { Plus, Trash2, Wrench, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

// --- NEC DATA TABLES ---
const wireData: Record<
  WireMaterial,
  {
    resistance: Record<string, number>;
    ampacity: Record<string, number>;
    area: Record<string, number>;
  }
> = {
  Copper: {
    resistance: {
      '14': 3.07,
      '12': 1.93,
      '10': 1.21,
      '8': 0.764,
      '6': 0.491,
      '4': 0.308,
      '2': 0.194,
      '1/0': 0.122,
      '2/0': 0.0967,
      '4/0': 0.0608,
    },
    ampacity: {
      '14': 15,
      '12': 20,
      '10': 30,
      '8': 50,
      '6': 65,
      '4': 85,
      '2': 115,
      '1/0': 150,
      '2/0': 175,
      '4/0': 230,
    },
    area: {
      '14': 0.0097,
      '12': 0.0133,
      '10': 0.0211,
      '8': 0.0366,
      '6': 0.0507,
      '4': 0.0824,
      '2': 0.1158,
      '1/0': 0.1855,
      '2/0': 0.2223,
      '4/0': 0.3237,
    },
  },
  Aluminum: {
    resistance: {
      '12': 3.18,
      '10': 2.0,
      '8': 1.26,
      '6': 0.808,
      '4': 0.508,
      '2': 0.319,
      '1/0': 0.201,
      '2/0': 0.159,
      '4/0': 0.1,
    },
    ampacity: {
      '12': 15,
      '10': 25,
      '8': 40,
      '6': 50,
      '4': 65,
      '2': 90,
      '1/0': 120,
      '2/0': 135,
      '4/0': 180,
    },
    area: {
      '12': 0.0133,
      '10': 0.0211,
      '8': 0.0366,
      '6': 0.0507,
      '4': 0.0824,
      '2': 0.1158,
      '1/0': 0.1855,
      '2/0': 0.2223,
      '4/0': 0.3237,
    },
  },
};

const conduitCapacity: Record<string, number> = {
  '1/2"': 0.12,
  '3/4"': 0.21,
  '1"': 0.34,
  '1 1/4"': 0.59,
  '1 1/2"': 0.81,
  '2"': 1.34,
};

const wireGaugesOrdered: WireGauge[] = [
  '14',
  '12',
  '10',
  '8',
  '6',
  '4',
  '2',
  '1/0',
  '2/0',
  '4/0',
];

const tempFactors: Record<AmbientTemp, number> = {
  'Standard (86°F)': 1.0,
  'Warm (104°F)': 0.91,
  'Hot Attic (122°F)': 0.82,
  'Extreme (140°F)': 0.71,
};

const protectionColors: Record<ProtectionType, string> = {
  Standard: 'bg-slate-500',
  GFCI: 'bg-emerald-500',
  AFCI: 'bg-purple-500',
  Dual: 'bg-orange-500',
};

interface ElectricalCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const ElectricalCalculator: React.FC<ElectricalCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');

  // Sprint 30, story-151 review Critical #3. Previously module-level hardcoded English
  // `SelectOption[]` arrays, invisible to the guard's object-literal-only check. `value` fields
  // are unchanged (they are `WireMaterial`/`WireType`/`ProtectionType`/`AmbientTemp` literal
  // union members read elsewhere as data keys); only the rendered `label` is translated.
  const materialOptions: SelectOption[] = [
    { value: 'Copper', label: tCalc.electrical.materialCopper },
    { value: 'Aluminum', label: tCalc.electrical.materialAluminum },
  ];

  const voltageOptions: SelectOption[] = [
    { value: '120', label: '120V' },
    { value: '240', label: '240V' },
  ];

  const wireTypeOptions: SelectOption[] = [
    { value: 'THHN', label: tCalc.electrical.wireTypeThhn },
    { value: 'Romex', label: 'Romex (NM-B)' },
  ];

  const protectionOptions: SelectOption[] = [
    { value: 'Standard', label: tCalc.electrical.protectionStandard },
    { value: 'GFCI', label: 'GFCI' },
    { value: 'AFCI', label: 'AFCI' },
    { value: 'Dual', label: tCalc.electrical.protectionDual },
  ];

  const ambientTempOptions: SelectOption[] = [
    { value: 'Standard (86°F)', label: tCalc.electrical.ambientStandard },
    { value: 'Warm (104°F)', label: tCalc.electrical.ambientWarm },
    { value: 'Hot Attic (122°F)', label: tCalc.electrical.ambientHotAttic },
    { value: 'Extreme (140°F)', label: tCalc.electrical.ambientExtreme },
  ];

  const maxDropOptions: SelectOption[] = [
    { value: '2', label: tCalc.electrical.maxDropRecommended },
    { value: '3', label: tCalc.electrical.maxDropNecMax },
    { value: '4', label: '4%' },
    { value: '5', label: '5%' },
  ];

  const [runs, setRuns] = useState<ElectricalRun[]>([
    {
      id: 1,
      label: tCalc.electrical.kitchenCircuit,
      material: 'Copper',
      voltage: 120,
      current: '20',
      wireGauge: '12',
      runLength: '50',
      type: 'Romex',
      protection: 'GFCI',
      ambientTemp: 'Standard (86°F)',
    },
  ]);

  const [maxDropAllowed, setMaxDropAllowed] = useState<number>(3);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newRun, setNewRun] = useState<Omit<ElectricalRun, 'id'>>({
    label: '',
    material: 'Copper',
    voltage: 120,
    current: '15',
    wireGauge: '14',
    runLength: '50',
    type: 'Romex',
    protection: 'Standard',
    ambientTemp: 'Standard (86°F)',
  });

  // Get available wire gauges for a material
  const getAvailableGauges = (material: WireMaterial): SelectOption[] => {
    const available = Object.keys(wireData[material].resistance);
    return wireGaugesOrdered
      .filter((g) => available.includes(g))
      .map((g) => ({ value: g, label: `${g} AWG` }));
  };

  // Update a run
  const updateRun = (
    id: number,
    field: keyof ElectricalRun,
    value: string | number,
  ) => {
    setRuns(
      runs.map((run) => (run.id === id ? { ...run, [field]: value } : run)),
    );
  };

  // Delete a run
  const deleteRun = (id: number) => {
    if (runs.length > 1) {
      setRuns(runs.filter((r) => r.id !== id));
    }
  };

  // Add a new run
  const addRun = () => {
    if (!newRun.label.trim()) return;
    setRuns([...runs, { ...newRun, id: Date.now() }]);
    setNewRun({
      label: '',
      material: 'Copper',
      voltage: 120,
      current: '15',
      wireGauge: '14',
      runLength: '50',
      type: 'Romex',
      protection: 'Standard',
      ambientTemp: 'Standard (86°F)',
    });
    setShowAddForm(false);
  };

  // Auto-fix gauge for a run
  const autoFixGauge = (id: number) => {
    const run = runs.find((r) => r.id === id);
    if (!run) return;

    const currentNum = parseFloat(run.current) || 0;
    const runLengthNum = parseFloat(run.runLength) || 0;

    let bestGauge = run.wireGauge;
    const available = Object.keys(wireData[run.material].resistance);

    for (const gauge of wireGaugesOrdered) {
      if (!available.includes(gauge)) continue;
      if (
        wireGaugesOrdered.indexOf(gauge) <
        wireGaugesOrdered.indexOf(run.wireGauge)
      )
        continue;

      const drop =
        ((run.voltage > 120 ? 1.732 : 2) *
          runLengthNum *
          wireData[run.material].resistance[gauge] *
          currentNum) /
        1000;
      const ampSafe =
        currentNum <=
        wireData[run.material].ampacity[gauge] * tempFactors[run.ambientTemp];

      if (ampSafe && (drop / run.voltage) * 100 <= maxDropAllowed) {
        bestGauge = gauge as WireGauge;
        break;
      }
    }

    updateRun(id, 'wireGauge', bestGauge);
  };

  // Main calculations
  const results: ElectricalCalculatorResults = useMemo(() => {
    const runResults: ElectricalRunResult[] = runs.map((run) => {
      const mat = wireData[run.material];
      const currentNum = parseFloat(run.current) || 0;
      const runLengthNum = parseFloat(run.runLength) || 0;
      const conductors = run.voltage > 120 ? 3 : 2;
      const drop =
        ((run.voltage > 120 ? 1.732 : 2) *
          runLengthNum *
          mat.resistance[run.wireGauge] *
          currentNum) /
        1000;
      const dropPct = (drop / run.voltage) * 100;
      const safeAmp =
        mat.ampacity[run.wireGauge] * tempFactors[run.ambientTemp];

      // Conduit Fill
      const totalArea = mat.area[run.wireGauge] * conductors;
      const minConduit =
        Object.entries(conduitCapacity).find(
          ([, cap]) => cap >= totalArea,
        )?.[0] || 'Oversize Required';

      return {
        ...run,
        dropPct,
        deratedAmp: safeAmp,
        conductors,
        minConduit,
        isSafe: currentNum <= safeAmp && dropPct <= maxDropAllowed,
        breakerSize: Math.ceil(currentNum / 0.8 / 5) * 5,
      };
    });

    // BOM Aggregation
    const wire: Record<string, number> = {};
    const breakers: Record<string, number> = {};
    const conduit: Record<string, number> = {};

    runResults.forEach((r) => {
      const runLengthVal = parseFloat(r.runLength) || 0;
      const wKey = `${r.wireGauge} AWG ${r.material} ${r.type}`;
      wire[wKey] = (wire[wKey] || 0) + runLengthVal * r.conductors;

      const bKey = `${r.breakerSize}A ${r.voltage > 120 ? '2-Pole' : '1-Pole'} ${r.protection}`;
      breakers[bKey] = (breakers[bKey] || 0) + 1;

      if (r.type === 'THHN') {
        conduit[r.minConduit] = (conduit[r.minConduit] || 0) + runLengthVal;
      }
    });

    return {
      runs: runResults,
      bom: {
        summary: {
          total_runs: runs.length,
          total_wire_ft: Object.values(wire).reduce((a, b) => a + b, 0),
        },
        wire_inventory: Object.entries(wire).map(([item, qty]) => ({
          item,
          quantity_feet: qty,
        })),
        breaker_inventory: Object.entries(breakers).map(([item, qty]) => ({
          item,
          quantity: qty,
        })),
        conduit_inventory: Object.entries(conduit).map(([size, len]) => ({
          size,
          total_feet: len,
          sticks_10ft: Math.ceil(len / 10),
        })),
      },
    };
  }, [runs, maxDropAllowed]);

  // Convert BOM to materials for adding
  const getMaterialsFromBOM = (): MaterialFromCalculator[] => {
    const materials: MaterialFromCalculator[] = [];

    results.bom.wire_inventory.forEach((w) => {
      materials.push({
        amount: w.quantity_feet,
        amountType: 'linearFeet',
        amountSystem: 'imperial',
        suggestedName: w.item,
        resultLabel: `${tCalc.materials.wire}: ${w.item}`,
      });
    });

    results.bom.breaker_inventory.forEach((b) => {
      materials.push({
        amount: b.quantity,
        amountType: 'units',
        amountSystem: 'imperial',
        suggestedName: `${tCalc.electrical.breakerMaterial} ${b.item}`,
        resultLabel: `${tCalc.electrical.breakerMaterial}: ${b.item}`,
      });
    });

    results.bom.conduit_inventory.forEach((c) => {
      materials.push({
        amount: c.sticks_10ft,
        amountType: 'units',
        amountSystem: 'imperial',
        suggestedName: `${c.size} ${tCalc.electrical.conduitTenFtSticks}`,
        resultLabel: `${tCalc.materials.conduit}: ${c.size}`,
      });
    });

    return materials;
  };

  const allRunsSafe = results.runs.every((r) => r.isSafe);

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.electrical}
      </h3>
      <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-6">
        {tCalc.electrical.subtitle}
      </p>

      {/* Global Settings */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.electrical.maxVoltageDropAllowed} htmlFor="maxDrop" />
            <div className="mt-1">
              <ReactSelect
                options={maxDropOptions}
                value={maxDropOptions.find(
                  (opt) => opt.value === String(maxDropAllowed),
                )}
                onChange={(option) => {
                  if (option && 'value' in option) {
                    setMaxDropAllowed(parseInt(option.value as string, 10));
                  }
                }}
                isClearable={false}
                isSearchable={false}
              />
            </div>
          </div>
          <div className="flex items-end">
            <div
              className={`w-full p-3 rounded-lg text-center font-medium ${
                allRunsSafe
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400'
              }`}>
              {allRunsSafe ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" /> {tCalc.electrical.allRunsSafe}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> {tCalc.electrical.someRunsNeedAttention}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Wire Runs List */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
            {tCalc.electrical.wireRuns} ({runs.length})
          </h4>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddForm(!showAddForm)}>
            {tCalc.actions.addRun}
          </Button>
        </div>

        {/* Add New Run Form */}
        {showAddForm && (
          <div className="mb-4 p-4 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg bg-indigo-50/50 dark:bg-indigo-900/20">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
              {tCalc.electrical.newWireRun}
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <Label label={tCalc.electrical.circuitLabel} htmlFor="newLabel" />
                <Input
                  id="newLabel"
                  type="text"
                  placeholder={tCalc.electrical.exampleCircuitLabel}
                  value={newRun.label}
                  onChange={(e) =>
                    setNewRun({ ...newRun, label: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.electrical.wireMaterial} htmlFor="newMaterial" />
                <div className="mt-1">
                  <ReactSelect
                    options={materialOptions}
                    value={materialOptions.find(
                      (opt) => opt.value === newRun.material,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        const newMaterial = option.value as WireMaterial;
                        const availableGauges = Object.keys(
                          wireData[newMaterial].resistance,
                        );
                        const currentGaugeAvailable = availableGauges.includes(
                          newRun.wireGauge,
                        );
                        setNewRun({
                          ...newRun,
                          material: newMaterial,
                          wireGauge: currentGaugeAvailable
                            ? newRun.wireGauge
                            : (availableGauges[0] as WireGauge),
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.materials.wireGauge} htmlFor="newGauge" />
                <div className="mt-1">
                  <ReactSelect
                    options={getAvailableGauges(newRun.material)}
                    value={getAvailableGauges(newRun.material).find(
                      (opt) => opt.value === newRun.wireGauge,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          wireGauge: option.value as WireGauge,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.electrical.voltage} htmlFor="newVoltage" />
                <div className="mt-1">
                  <ReactSelect
                    options={voltageOptions}
                    value={voltageOptions.find(
                      (opt) => opt.value === String(newRun.voltage),
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          voltage: parseInt(option.value as string, 10),
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.electrical.loadCurrentA} htmlFor="newCurrent" />
                <Input
                  id="newCurrent"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 20`}
                  value={newRun.current}
                  onChange={(e) =>
                    setNewRun({
                      ...newRun,
                      current: e.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.electrical.runLengthFt} htmlFor="newLength" />
                <Input
                  id="newLength"
                  type="number"
                  placeholder={`${tCalc.actions.examplePrefix}, 50`}
                  value={newRun.runLength}
                  onChange={(e) =>
                    setNewRun({
                      ...newRun,
                      runLength: e.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label label={tCalc.electrical.wireType} htmlFor="newType" />
                <div className="mt-1">
                  <ReactSelect
                    options={wireTypeOptions}
                    value={wireTypeOptions.find(
                      (opt) => opt.value === newRun.type,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          type: option.value as WireType,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.electrical.protectionType} htmlFor="newProtection" />
                <div className="mt-1">
                  <ReactSelect
                    options={protectionOptions}
                    value={protectionOptions.find(
                      (opt) => opt.value === newRun.protection,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          protection: option.value as ProtectionType,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div>
                <Label label={tCalc.electrical.ambientTemperature} htmlFor="newTemp" />
                <div className="mt-1">
                  <ReactSelect
                    options={ambientTempOptions}
                    value={ambientTempOptions.find(
                      (opt) => opt.value === newRun.ambientTemp,
                    )}
                    onChange={(option) => {
                      if (option && 'value' in option) {
                        setNewRun({
                          ...newRun,
                          ambientTemp: option.value as AmbientTemp,
                        });
                      }
                    }}
                    isClearable={false}
                    isSearchable={false}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="primary" size="md" onClick={addRun}>
                {tCalc.actions.addRun}
              </Button>
              <Button
                variant="tertiary"
                size="md"
                onClick={() => setShowAddForm(false)}>
                {tCalc.actions.cancel}
              </Button>
            </div>
          </div>
        )}

        {/* Run Cards - Compact Version */}
        <div className="space-y-3">
          {results.runs.map((run) => (
            <div
              key={run.id}
              className={`rounded-lg border-2 transition-all overflow-hidden ${
                !run.isSafe
                  ? 'border-rose-400 dark:border-rose-600'
                  : 'border-gray-200 dark:border-gray-700'
              }`}>
              {/* Row 1: Header with label, material, badges, actions */}
              <div
                className={`px-4 py-3 flex items-center justify-between gap-2 ${
                  !run.isSafe
                    ? 'bg-rose-50 dark:bg-rose-900/30'
                    : 'bg-gray-50 dark:bg-gray-800'
                }`}>
                <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                  <Input
                    type="text"
                    value={run.label}
                    onChange={(e) => updateRun(run.id, 'label', e.target.value)}
                    className="font-semibold border-none bg-transparent p-0 text-sm w-32 sm:w-40"
                    placeholder={tCalc.electrical.circuitLabel}
                  />
                  <div className="w-36">
                    <ReactSelect
                      options={materialOptions}
                      value={materialOptions.find(
                        (opt) => opt.value === run.material,
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          const newMaterial = option.value as WireMaterial;
                          const availableGauges = Object.keys(
                            wireData[newMaterial].resistance,
                          );
                          const currentGaugeAvailable = availableGauges.includes(
                            run.wireGauge,
                          );
                          updateRun(run.id, 'material', newMaterial);
                          if (!currentGaugeAvailable) {
                            updateRun(
                              run.id,
                              'wireGauge',
                              availableGauges[0] as WireGauge,
                            );
                          }
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <span
                    className={`text-xs font-medium text-white px-2 py-0.5 rounded whitespace-nowrap ${protectionColors[run.protection]}`}>
                    {run.protection}
                  </span>
                  {run.type === 'THHN' && (
                    <span className="text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded whitespace-nowrap">
                      {run.minConduit}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button type="button"
                    onClick={() => autoFixGauge(run.id)}
                    className={`p-1.5 rounded transition-colors ${
                      !run.isSafe
                        ? 'bg-rose-600 text-white hover:bg-rose-700'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                    title={tCalc.electrical.autoFixWireGauge}>
                    <Wrench className="w-4 h-4" />
                  </button>
                  <button type="button"
                    onClick={() => deleteRun(run.id)}
                    disabled={runs.length <= 1}
                    className="p-1.5 text-gray-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={tCalc.actions.deleteRun}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Row 2: Input fields in grid */}
              <div className="px-4 py-3 bg-white dark:bg-gray-900">
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.electrical.gauge}
                    </p>
                    <ReactSelect
                      options={getAvailableGauges(run.material)}
                      value={getAvailableGauges(run.material).find(
                        (opt) => opt.value === run.wireGauge,
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(run.id, 'wireGauge', option.value as string);
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.electrical.voltage}
                    </p>
                    <ReactSelect
                      options={voltageOptions}
                      value={voltageOptions.find(
                        (opt) => opt.value === String(run.voltage),
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(
                            run.id,
                            'voltage',
                            parseInt(option.value as string, 10),
                          );
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.electrical.current}
                    </p>
                    <Input
                      type="number"
                      value={run.current}
                      onChange={(e) =>
                        updateRun(run.id, 'current', e.target.value)
                      }
                      className="h-[38px]"
                      placeholder="A"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.dimensions.length}
                    </p>
                    <Input
                      type="number"
                      value={run.runLength}
                      onChange={(e) =>
                        updateRun(run.id, 'runLength', e.target.value)
                      }
                      className="h-[38px]"
                      placeholder="ft"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.electrical.typeLabel}
                    </p>
                    <ReactSelect
                      options={wireTypeOptions}
                      value={wireTypeOptions.find(
                        (opt) => opt.value === run.type,
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(run.id, 'type', option.value as string);
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {tCalc.electrical.temp}
                    </p>
                    <ReactSelect
                      options={ambientTempOptions}
                      value={ambientTempOptions.find(
                        (opt) => opt.value === run.ambientTemp,
                      )}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          updateRun(run.id, 'ambientTemp', option.value as string);
                        }
                      }}
                      isClearable={false}
                      isSearchable={false}
                    />
                  </div>
                </div>
                {/* Second row for Protection */}
                <div className="mt-2">
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <div className="col-span-2 sm:col-span-2">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                        {tCalc.electrical.protection}
                      </p>
                      <ReactSelect
                        options={protectionOptions}
                        value={protectionOptions.find(
                          (opt) => opt.value === run.protection,
                        )}
                        onChange={(option) => {
                          if (option && 'value' in option) {
                            updateRun(run.id, 'protection', option.value as string);
                          }
                        }}
                        isClearable={false}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Calculated results - Compact horizontal layout */}
              <div
                className={`px-4 py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm ${
                  !run.isSafe
                    ? 'bg-rose-100 dark:bg-rose-900/40'
                    : 'bg-emerald-50 dark:bg-emerald-900/20'
                }`}>
                <div className="flex items-center gap-1">
                  {run.isSafe ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  )}
                  <span
                    className={`font-semibold ${
                      run.dropPct <= maxDropAllowed
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-rose-700 dark:text-rose-400'
                    }`}>
                    {run.dropPct.toFixed(1)}% {tCalc.electrical.dropSuffix}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-gray-700 dark:text-gray-300">
                  <span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {tCalc.electrical.breaker}{' '}
                    </span>
                    <span className="font-semibold">{run.breakerSize}A</span>
                  </span>
                  <span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {tCalc.electrical.limit}{' '}
                    </span>
                    <span className="font-semibold">
                      {run.deratedAmp.toFixed(0)}A
                    </span>
                  </span>
                  <span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {tCalc.electrical.cond}{' '}
                    </span>
                    <span className="font-semibold">{run.conductors}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BOM Summary */}
      {results.bom.wire_inventory.length > 0 && (
        <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {tCalc.actions.billOfMaterialsSummary}
          </h4>

          {/* Wire Inventory */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tCalc.electrical.wireInventory}
            </h5>
            <div className="space-y-2">
              {results.bom.wire_inventory.map((w, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {w.item}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {w.quantity_feet.toLocaleString()} ft
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: w.quantity_feet,
                            amountType: 'linearFeet',
                            amountSystem: 'imperial',
                            suggestedName: w.item,
                            resultLabel: `${tCalc.materials.wire}: ${w.item}`,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conduit Inventory */}
          {results.bom.conduit_inventory.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {tCalc.electrical.conduitTenFtSticks}
              </h5>
              <div className="space-y-2">
                {results.bom.conduit_inventory.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {tCalc.materials.conduit} {c.size}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                        {c.sticks_10ft} {tCalc.electrical.sticksLabel} ({c.total_feet} ft)
                      </span>
                      {onAddToMaterial && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="md"
                          leftIcon={<Plus className="w-3 h-3" />}
                          onClick={() =>
                            onAddToMaterial({
                              amount: c.sticks_10ft,
                              amountType: 'units',
                              amountSystem: 'imperial',
                              suggestedName: `${c.size} ${tCalc.electrical.conduitTenFtSticks}`,
                              resultLabel: `${tCalc.materials.conduit}: ${c.size}`,
                            })
                          }>
                          {tCalc.actions.add}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Breaker Inventory */}
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {tCalc.electrical.breakers}
            </h5>
            <div className="space-y-2">
              {results.bom.breaker_inventory.map((b, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {b.item}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {b.quantity}x
                    </span>
                    {onAddToMaterial && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        leftIcon={<Plus className="w-3 h-3" />}
                        onClick={() =>
                          onAddToMaterial({
                            amount: b.quantity,
                            amountType: 'units',
                            amountSystem: 'imperial',
                            suggestedName: `${tCalc.electrical.breakerMaterial} ${b.item}`,
                            resultLabel: `${tCalc.electrical.breakerMaterial}: ${b.item}`,
                          })
                        }>
                        {tCalc.actions.add}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add All Materials Button */}
          {onAddAllMaterials && (
            <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
              <Button
                type="button"
                variant="primary"
                fullWidth
                size="lg"
                leftIcon={<Plus className="w-5 h-5" />}
                onClick={() => onAddAllMaterials(getMaterialsFromBOM())}>
                {tCalc.actions.addAllMaterials} ({getMaterialsFromBOM().length} {tCalc.actions.items})
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ElectricalCalculator;
